import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout, { Container } from "../components/layout";
import axios from "axios";
import { apiurl } from "../config/config";
import { SuwasthiLoader } from "../components/loader";
import Breadcrumb from "../components/breadcrumb";
import VerifySectionStatus from "../components/verifySectionStatus";
import { useUser } from "../config/userProvider";
import { toast } from "react-toastify";
import heic2any from "heic2any";

const ManageEmployee = () => {
  const { userData } = useUser();
  const { employeeObjId } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [documentLink, setDocumentLink] = useState("");
  const [message, setMessage] = useState("");
  const [remarks, setRemarks] = useState("");
  const [status, setStatus] = useState(""); // "verified" or "rejected"

  const verifierObjId = userData._id;

  const handleVerify = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${apiurl}/api/employee/verify-employee`, {
        employeeObjId: employeeObjId,
        status,
        remarks,
        verifierObjId,
      });
      setMessage(res.data.message);
      toast.success("Verification successful!");
      window.location.reload();
    } catch (error) {
      setMessage(error.response?.data?.message || "Error verifying employee");
    } finally {
      setLoading(false);
    }
  };

  const resendToken = async () => {
    setLoading(true);
    setMessage("");

    try {
      const res = await axios.post(
        `${apiurl}/api/employee/resend-employee-token`,
        {
          employeeObjIds: [employeeObjId],
        }
      );

      setMessage("Token resent successfully!");
    } catch (error) {
      console.error(error);
      setMessage(error.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${apiurl}/api/employee/get/${employeeObjId}`)
      .then((res) => {
        setEmployee(res?.data?.employee);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching employee:", error);
        setLoading(false);
      });
  }, [employeeObjId]);

  const openModal = (link) => {
    setDocumentLink(link);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setDocumentLink("");
  };

  const renderValue = (value) => value || "NA";
  const borderColor =
    employee?.verificationStatus === "verified"
      ? "border-green-500"
      : employee?.verificationStatus === "rejected"
      ? "border-red-500"
      : "border-yellow-500";

  if (loading) {
    return (
      <Layout>
        <SuwasthiLoader />
      </Layout>
    );
  }

  if (!employee) {
    return (
      <Layout>
        <div className="text-center text-red-500 font-semibold mt-10">
          Employee not found.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Breadcrumb page="Employee Details" />
      <Container overflow="overflow-scroll">
        <div className="p-4 bg-gray-50 min-h-screen mb-10">
          {/* <VerifySectionStatus
            employeeId={employee._id}
            sectionName="personalDetails"
            currentStatus={employee.personalDetails?.verificationStatus}
            currentRemarks={employee.personalDetails?.verificationRemarks}
            onUpdate={(newStatus, newRemarks) => {
              setEmployee((prev) => ({
                ...prev,
                personalDetails: {
                  ...prev.personalDetails,
                  verificationStatus: newStatus,
                  verificationRemarks: newRemarks,
                },
              }));
            }}

          /> */}

          <div className="bg-white rounded-2xl shadow-sm border p-6 w-full mb-4 mx-auto">
            {/* Header Section: Photo + Basic Info */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-6">
              {/* Employee Photo */}
              <img
                src={employee?.documents?.photo?.link || "/images/user.png"}
                alt={
                  employee?.personalDetails?.firstName +
                  " " +
                  employee?.personalDetails?.lastName
                }
                className="w-24 h-24 rounded-full object-cover border-2 border-gray-300 p-1"
              />

              {/* Name, Code, Phone */}
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-800 capitalize">
                  {`${employee?.personalDetails?.firstName || ""} ${
                    employee?.personalDetails?.lastName || ""
                  }`.trim() || "N/A"}
                </h2>
                <p className="text-sm text-gray-500">
                  Employee Code:{" "}
                  <span className="font-medium">
                    {employee?.employeeCode || "N/A"}
                  </span>
                </p>
                <p className="text-sm text-gray-500">
                  Phone:{" "}
                  <span className="font-medium">
                    {employee?.personalDetails?.phone || "N/A"}
                  </span>
                </p>
                <p className="text-sm text-gray-500">
                  Email:{" "}
                  <span className="font-medium">
                    {employee?.personalDetails?.email || "N/A"}
                  </span>
                </p>
              </div>

              {/* Status Badge */}
              <div className="sm:ml-auto">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    employee?.employeeStatus?.toLowerCase() === "active"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {employee?.employeeStatus || "N/A"}
                </span>
              </div>
            </div>

            {/* Grid Info Section */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm text-gray-700">
              <p>
                <strong>ID:</strong> {renderValue(employee?.id)}
              </p>
              <p>
                <strong>Type:</strong> {renderValue(employee?.employeeType)}
              </p>
              <p>
                <strong>Department:</strong> {renderValue(employee?.department)}
              </p>
              <p>
                <strong>Designation:</strong>{" "}
                {renderValue(employee?.designation)}
              </p>
              <p>
                <strong>Office Location:</strong>{" "}
                {renderValue(employee?.officeLocation)}
              </p>
              <p>
                <strong>Joining Date:</strong>{" "}
                {employee?.employeeJoiningDate
                  ? new Date(employee.employeeJoiningDate).toLocaleDateString()
                  : "NA"}
              </p>
              <p>
                <strong>Leaving Date:</strong>{" "}
                {employee?.employeeLeavingDate
                  ? new Date(employee.employeeLeavingDate).toLocaleDateString()
                  : "NA"}
              </p>
              <p>
                <strong>Gender:</strong>{" "}
                {renderValue(employee?.personalDetails?.gender)}
              </p>
              <p>
                <strong>Marital Status:</strong>{" "}
                {renderValue(employee?.personalDetails?.maritalStatus)}
              </p>
              <p>
                <strong>Date of Birth:</strong>{" "}
                {employee?.personalDetails?.dateOfBirth
                  ? new Date(
                      employee.personalDetails.dateOfBirth
                    ).toLocaleDateString()
                  : "NA"}
              </p>
              <p>
                <strong>Blood Group:</strong>{" "}
                {renderValue(employee?.personalDetails?.bloodGroup)}
              </p>
              <p>
                <strong>Nationality:</strong>{" "}
                {renderValue(employee?.personalDetails?.nationality)}
              </p>
            </div>
          </div>
          <div
            className={`bg-white rounded-2xl border-2 ${borderColor} shadow-lg p-6 w-full mx-auto my-4`}
          >
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Verify Employee
            </h2>

            <p>
              <span className="font-medium">Name:</span>{" "}
              {`${employee?.personalDetails?.firstName || ""} ${
                employee?.personalDetails?.lastName || ""
              }`.trim() || "N/A"}
            </p>
            <p>
              <span className="font-medium">Email:</span>{" "}
              {employee.personalDetails?.email}
            </p>
            <p>
              <span className="font-medium">Status:</span>{" "}
              {employee.verificationStatus}
            </p>

            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks..."
              className="mt-4 w-full p-2 border rounded-md text-sm"
            />

            <div className="mt-4 space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="status"
                  value="verified"
                  checked={status === "verified"}
                  onChange={() => setStatus("verified")}
                  className="text-green-600 focus:ring-green-500"
                />
                <span className="text-sm text-gray-700">Mark as Verified</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="status"
                  value="rejected"
                  checked={status === "rejected"}
                  onChange={() => setStatus("rejected")}
                  className="text-red-600 focus:ring-red-500"
                />
                <span className="text-sm text-gray-700">Mark as Rejected</span>
              </label>
            </div>

            <button
              onClick={handleVerify}
              disabled={loading || !status}
              className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg"
            >
              {loading ? "Verifying..." : "Submit Verification"}
            </button>

            {message && (
              <p className="mt-3 text-sm text-center text-blue-700">
                {message}
              </p>
            )}
          </div>

          <div className="mx-auto p-6 flex justify-between w-full bg-white border rounded-xl mb-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-4">
              Resend Employee Token
            </h2>
            <div>
              <button
                onClick={resendToken}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition disabled:opacity-50"
                disabled={loading}
              >
                {loading ? "Sending..." : "Resend Token"}
              </button>

              {message && (
                <p className="mt-4 text-sm text-gray-700">{message}</p>
              )}
            </div>
          </div>
          <EmployeeDocumentUpload employee={employee} />
        </div>
      </Container>
    </Layout>
  );
};

export default ManageEmployee;

const documentList = [
  { key: "passportSizePhotos", label: "Passport Size Photo" },
  //   { key: "signedEmployeeAgreement", label: "Signed Employee Agreement" },
  //   { key: "declarationGoodHealth", label: "Declaration of Good Health" },
  //   { key: "residenceProof", label: "Residence Proof" },
  { key: "appointmentOfferLetter", label: "Appointment / Offer Letter" },
  { key: "bankStatement", label: "Bank Statement" },
  //   { key: "resume", label: "Resume / CV" },
  //   { key: "photo", label: "Personal Photo" },
  { key: "aadharCard", label: "Aadhar Card" },
  { key: "panCard", label: "PAN Card" },
  //   { key: "passport", label: "Passport" },
  { key: "cancelledChequeOrPassbook", label: "Cancelled Cheque / Passbook" },
];

const EmployeeDocumentUpload = ({employee}) => {
  // const navigate = useNavigate();
  const [documents, setDocuments] = useState(employee.documents);
  const [uploadingKey, setUploadingKey] = useState("");
  const [error, setError] = useState("");


  const handleFileUpload = async (e, docKey) => {
    const file = e.target.files[0];
    if (!file) return;

    let finalFile = file;

    if (file.type === "image/heic" || file.name.endsWith(".heic")) {
      try {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.9,
        });
        finalFile = new File(
          [convertedBlob],
          file.name.replace(/\.heic$/, ".jpg"),
          { type: "image/jpeg" }
        );
      } catch (conversionError) {
        setError("Error converting HEIC file.");
        alert("Could not convert HEIC image to JPG. Please try another file.");
        return;
      }
    }

    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    if (!allowedTypes.includes(finalFile.type)) {
      setError(
        `Unsupported file type: ${finalFile.type}. Please upload JPG, PNG, or PDF.`
      );
      alert(
        `Unsupported file type: ${finalFile.type}. Please upload JPG, PNG, or PDF.`
      );
      return;
    }

    setUploadingKey(docKey);

    const formData = new FormData();
    formData.append("file", finalFile);
    formData.append(
      "folder",
      `${employee?.identification?.aadharNumber}` || "employee-documents"
    );

    try {
      const response = await axios.post(
        `${apiurl}/api/media/employeeFileUpload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        }
      );

      if (response?.data?.success) {
        setDocuments((prev) => ({
          ...prev,
          [docKey]: { link: response.data.fileUrl, uploaded: true },
        }));
      } else {
        alert("Upload failed. Please try again.");
      }
    } catch (error) {
      console.error("Full error:", JSON.stringify(error, null, 2));
      if (error.response) {
        setError(
          `Server responded: ${
            error.response.data?.message || JSON.stringify(error.response.data)
          }`
        );
      } else if (error.request) {
        setError("No response from server. Network or server error.");
      } else {
        setError(`Error: ${error.message}`);
      }
      alert(error.message);
    } finally {
      setUploadingKey("");
    }
  };

  const handleRemoveFile = (docKey) => {
    setDocuments((prev) => ({
      ...prev,
      [docKey]: { link: null, uploaded: false },
    }));
  };

  const handleSubmit = async (e) => {
    console.log("documents", documents);
    e.preventDefault();
    try {
      await axios.patch(
        `${apiurl}/api/employee/update-employee-details/${employee._id}`,
        { documents }
      );
      alert("Documents submitted successfully!");
      // navigate(`/employees/thankyou/${employeeToken}`);
      // navigate(`/employees/thankyou/${employeeToken}`);
      // navigate(`/employees/thankyou/${employeeToken}/${employeeDetails?.employeeId}`);
    } catch (error) {
      console.error("Submit failed:", error);
      alert("Submission failed. Try again.");
    }
  };

  return (
    <>
      <div className="mx-auto p-6 bg-white shadow-xl rounded-2xl mt-10 mb-10">
        <h2 className="text-2xl font-bold text-center mb-6">
          Upload Your Documents
        </h2>

        {error && (
          <div className="bg-red-100 text-red-800 p-2 rounded mt-2">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {documentList.map(({ key, label }) => (
              <div
                key={key}
                className="border rounded-xl p-4 flex flex-col gap-2 shadow-sm"
              >
                <label className="font-medium text-gray-700">{label}</label>

                {documents[key]?.uploaded ? (
                  <div className="flex justify-between items-center bg-green-100 text-green-700 p-2 rounded">
                    <a
                      href={documents[key].link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-sm truncate"
                    >
                      View Uploaded File
                    </a>
                    <button
                      type="button"
                      className="text-red-500 text-sm hover:underline"
                      onClick={() => handleRemoveFile(key)}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="file"
                      //   accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      accept="image/jpeg,image/png,application/pdf"
                      onChange={(e) => handleFileUpload(e, key)}
                      className="border rounded p-2 w-full text-sm"
                    />
                    {uploadingKey === key && (
                      <div>
                        <p>Uploading...</p>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-500 h-2 rounded-full"
                            style={{
                              width: `${documents[key]?.progress || 0}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          <button
            type="submit"
            onClick={handleSubmit}
            // disabled={Object.values(documents).some((doc) => !doc.uploaded)}
            className="mt-8 bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-xl w-full font-semibold"
          >
            Submit Documents
          </button>
        </form>
      </div>
    </>
  );
};
